import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { layoutQuery } from "#modules/layouts/infra/layout.query";
import { invalidateQuoteRequestLinesDebounced } from "#modules/quoteRequestLines/infra/invalidateQuoteRequestLinesDebounced";

import { createMaterialLayout } from "../infra/createMaterialLayout.api";

export const useCreateMaterialLayout = ({
  quoteRequestLineId,
  layoutId,
  options,
}: {
  quoteRequestLineId: string;
  layoutId: string | undefined | null;
  options?: {
    onSuccess?: () => void;
    onError?: (err: Error) => void;
    onMutate?: () => void;
  };
}) => {
  const queryClient = useQueryClient();

  const invalidateLineWithLayout = ({
    quoteRequestLineIdToInvalidate,
    layoutIdToInvalidate,
  }: {
    quoteRequestLineIdToInvalidate: string;
    layoutIdToInvalidate: string | null | undefined;
  }) => {
    if (quoteRequestLineIdToInvalidate) {
      invalidateQuoteRequestLinesDebounced({
        queryClient,
        quoteRequestLineId: quoteRequestLineIdToInvalidate,
      });
    }
    if (layoutIdToInvalidate) {
      void queryClient.invalidateQueries(layoutQuery(layoutIdToInvalidate));
    }
  };

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: createMaterialLayout,
    onMutate: () => {
      options?.onMutate?.();
    },
    onSuccess: (data) => {
      invalidateLineWithLayout({
        quoteRequestLineIdToInvalidate: quoteRequestLineId,
        layoutIdToInvalidate: layoutId,
      });

      data.otherCreatedData.forEach(
        ({ quoteRequestLineId: otherQuoteRequestLineId, materialLayout: otherMaterialLayout }) => {
          invalidateLineWithLayout({
            quoteRequestLineIdToInvalidate: otherQuoteRequestLineId,
            layoutIdToInvalidate: otherMaterialLayout.layoutId,
          });
        },
      );

      options?.onSuccess?.();
    },
    onError: (err: Error) => {
      showErrorToast({ title: err.message });
      errorHandler(err);

      options?.onError?.(err);
    },
  });

  return {
    createMaterialLayout: mutate,
    createMaterialLayoutAsync: mutateAsync,
    ...rest,
  };
};
